import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedBriefcaseLeaderboard = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const limit = parseInt(searchParams.get('limit')) || 20;
  const showHeader = searchParams.get('header') !== 'false';
  const showBriefcases = searchParams.get('briefcases') !== 'false';
  const showRank = searchParams.get('rank') !== 'false';
  const style = searchParams.get('style') || 'compact'; // compact, full, minimal
  const accentColor = searchParams.get('accent') || '#ffd700';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 3000;
  const animate = searchParams.get('animate') !== 'false';

  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastAwarded, setLastAwarded] = useState(null);
  const socketRef = useRef(null);
  const prevRanksRef = useRef({});

  // Fetch initial state
  const fetchState = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/briefcase/state`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          updateState(data.state);
        }
      }
    } catch (error) {
      console.error('Error fetching briefcase state:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateState = (newState) => {
    if (!newState || !newState.teams) return;

    // Track rank changes for animation
    const sortedTeams = [...newState.teams].sort((a, b) =>
      (b.totalValue || 0) - (a.totalValue || 0)
    );

    const currentRanks = {};
    sortedTeams.forEach((team, index) => {
      const prevRank = prevRanksRef.current[team.id];
      currentRanks[team.id] = index + 1;
      team.rankChange = prevRank ? prevRank - (index + 1) : 0;
    });
    prevRanksRef.current = currentRanks;

    setGameState({ ...newState, teams: sortedTeams });
  };

  useEffect(() => {
    fetchState();

    // Set up socket for real-time updates
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('briefcase-state-update', (data) => {
      updateState(data);
    });

    socketRef.current.on('briefcase-awarded', (data) => {
      setLastAwarded({
        teamId: data.teamId,
        teamName: data.teamName,
        value: data.briefcase?.value,
        timestamp: Date.now()
      });

      // Clear after animation
      setTimeout(() => setLastAwarded(null), 3000);
    });

    // Fallback polling
    const interval = setInterval(fetchState, refreshInterval);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(interval);
    };
  }, [refreshInterval]);

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value < 0) return `-$${Math.abs(value).toLocaleString()}`;
    return `$${value.toLocaleString()}`;
  };

  const getBriefcaseIcon = (briefcase) => {
    switch (briefcase.type) {
      case 'positive': return '💰';
      case 'negative': return '💸';
      case 'curtain': return briefcase.revealed ? '🎭' : '❓';
      case 'double': return '✕2';
      case 'half': return '½';
      case 'swap': return '🔄';
      default: return '💼';
    }
  };

  if (loading) {
    return (
      <div className="embed-container embed-briefcase-leaderboard" style={{ background: bg }}>
        <div className="embed-loading">Loading briefcase leaderboard...</div>
      </div>
    );
  }

  if (!gameState?.active) {
    return (
      <div className="embed-container embed-briefcase-leaderboard" style={{ background: bg }}>
        <div className="embed-loading" style={{ flexDirection: 'column', gap: '10px' }}>
          <span style={{ fontSize: '48px' }}>💼</span>
          <span>A Brief Case</span>
          <span style={{ fontSize: '14px', color: '#666' }}>Game not started</span>
        </div>
      </div>
    );
  }

  const teams = (gameState.teams || []).slice(0, limit);

  return (
    <div
      className={`embed-container embed-briefcase-leaderboard ${theme} style-${style}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && (
        <div className="briefcase-header" style={{ borderColor: accentColor }}>
          <span className="briefcase-title">💼 A BRIEF CASE</span>
          <span className="briefcase-round">LIVE</span>
        </div>
      )}

      {lastAwarded && (
        <div className="briefcase-award-banner">
          <span className="award-icon">🎉</span>
          <span className="award-text">
            <strong>{lastAwarded.teamName}</strong> won{' '}
            <strong>{formatCurrency(lastAwarded.value)}</strong>!
          </span>
        </div>
      )}

      <div className="briefcase-list">
        {teams.map((team, index) => {
          const rank = index + 1;
          const isTop3 = rank <= 3;
          const justAwarded = lastAwarded?.teamId === team.id;

          return (
            <div
              key={team.id}
              className={`briefcase-item ${isTop3 ? 'top-three' : ''} ${animate && team.rankChange !== 0 ? 'rank-changed' : ''} ${justAwarded ? 'just-awarded' : ''}`}
              style={{
                '--rank-change': team.rankChange,
                '--accent': accentColor,
              }}
            >
              {showRank && (
                <div className={`briefcase-rank ${isTop3 ? `rank-${rank}` : ''}`}>
                  {isTop3 ? getRankBadge(rank) : <span className="rank-number">{rank}</span>}
                </div>
              )}

              <div className="briefcase-info">
                <span className="briefcase-team-name">{team.name}</span>
                {showBriefcases && team.briefcases && team.briefcases.length > 0 && (
                  <div className="briefcase-icons">
                    {team.briefcases.slice(-10).map((bc, i) => (
                      <span
                        key={i}
                        className={`briefcase-icon ${bc.type}`}
                        title={formatCurrency(bc.value)}
                      >
                        {getBriefcaseIcon(bc)}
                      </span>
                    ))}
                    {team.briefcases.length > 10 && (
                      <span className="briefcase-more">+{team.briefcases.length - 10}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="briefcase-value">
                <span className="briefcase-total">{formatCurrency(team.totalValue || 0)}</span>
                <span className="briefcase-count">
                  {team.briefcases?.length || 0} briefcase{(team.briefcases?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>

              {animate && team.rankChange !== 0 && (
                <div className={`rank-change-indicator ${team.rankChange > 0 ? 'up' : 'down'}`}>
                  {team.rankChange > 0 ? `↑${team.rankChange}` : `↓${Math.abs(team.rankChange)}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmbedBriefcaseLeaderboard;
